import { Box, SxProps } from '@mui/material';
import { TextBlock } from 'common/ui/TextBlock';
import { CampaignCreationCSVFileUpload } from './CampaignCreationCSVFileUpload';
import { useCsvParser } from 'hooks/useCsvParser';
import { FormikProps, useFormik } from 'formik';
import Yup from 'utils/validation';
import { useOrganizationConfig } from 'core/stores/hooks/useOrganizationConfig';
import { ColumnsMapper } from 'components/ColumnsMapper';
import { useEffect } from 'react';
import { useAmplifyUploadData } from 'core/services/amplify/storage/useAmplifyUploadFile';
import { S3Bucket } from 'core/services/amplify/config/constants';
import { useCsvColumnMatcher } from 'pages/CampaignCreation/hooks/useCsvColumnMatcher';
import { Channels } from 'generated-graphql/types';
import { ToastType, useToast } from 'services/toast';
import { FormValues } from 'pages/CampaignCreation/types';
import { CampaignCreationNextButton } from 'modules/campaign-creation/components/CampaignCreationNextButton';

const sectionTitleContainer: SxProps = {
  mb: 1
};

const sectionContainer: SxProps = {
  mt: 2
};

const getValidationSchema = ({
  isPhoneNumberRequired,
  isEmailRequired
}: {
  isPhoneNumberRequired: boolean;
  isEmailRequired: boolean;
}) => {
  return Yup.object({
    csvColumns: Yup.object({
      firstName: Yup.string().required('First name is required'),
      lastName: Yup.string().optional().nullable(),
      phoneNumber: isPhoneNumberRequired
        ? Yup.string().required('Phone number is required')
        : Yup.string().optional(),
      email: isEmailRequired
        ? Yup.string().required('Email is required')
        : Yup.string().optional()
    }).required(),
    csvS3Key: Yup.string().optional().nullable()
  });
};

type FormikFormValues = Yup.InferType<ReturnType<typeof getValidationSchema>>;

export type CampaignCreationCSVColumns = FormikFormValues['csvColumns'];

// NOTE: We pass the state down instead of using the store to allow this component to be used in both the create and add more candidates flow.
export const CampaignCreationCSVUpload: React.FC<{
  channel: Channels | null;
  // campaign-level fallback value; null = inherit the org setting
  emailFallbackEnabled: boolean | null;
  csvImportState: {
    csvColumns: CampaignCreationCSVColumns;
    csvS3Key: string;
  } | null;
  onCSVImportCandidates: (values: {
    csvS3Key: string;
    csvColumns: CampaignCreationCSVColumns;
  }) => Promise<void>;
  onCSVImportSkip: (() => void) | null;
}> = ({
  channel,
  emailFallbackEnabled,
  csvImportState,
  onCSVImportCandidates,
  onCSVImportSkip
}) => {
  const organizationConfig = useOrganizationConfig();

  const { parseCsv, file, numRows, columns, csvData } = useCsvParser();
  const { uploadData, isUploading } = useAmplifyUploadData({
    bucket: S3Bucket.CSV
  });

  const { showToast } = useToast();

  // NOTE: This allows the step to be in which ever order.
  const isEmailRequiredWithFallback =
    channel === Channels.Email ||
    (emailFallbackEnabled ?? !!organizationConfig?.emailChannelFallbackEnabled);
  const isPhoneNumberRequired =
    channel === Channels.Whatsapp || channel === Channels.Sms;

  const formik = useFormik({
    initialValues: {
      csvColumns: {
        firstName: csvImportState?.csvColumns?.firstName || '',
        lastName: csvImportState?.csvColumns?.lastName || '',
        phoneNumber: csvImportState?.csvColumns?.phoneNumber || '',
        email: csvImportState?.csvColumns?.email || ''
      },
      csvS3Key: csvImportState?.csvS3Key || null
    },
    validationSchema: getValidationSchema({
      isPhoneNumberRequired,
      isEmailRequired: isEmailRequiredWithFallback
    }),
    onSubmit: (values) => {
      if (isUploading) return;

      if (!values.csvColumns.email && !values.csvColumns.phoneNumber) {
        showToast({
          message: 'At least one of email or phone number is required',
          type: ToastType.Error
        });
        return;
      }

      if (!values.csvS3Key) {
        showToast({
          message:
            'Failed to upload your file. Please try re-uploading your CSV.',
          type: ToastType.Error
        });
        return;
      }

      onCSVImportCandidates({
        csvS3Key: values.csvS3Key || '',
        csvColumns: values.csvColumns
      });
    }
  });

  useCsvColumnMatcher({
    columns,
    // TODO: Fix this typing
    formik: formik as unknown as FormikProps<FormValues>
  });

  useEffect(() => {
    const handleColumnsMapping = async () => {
      const { firstName, lastName, phoneNumber, email } =
        formik.values.csvColumns;

      if (
        formik.errors.csvColumns &&
        Object.keys(formik.errors.csvColumns).length
      ) {
        return;
      }

      if (!file) return;

      const metadata = {
        columnsMapping: JSON.stringify({
          firstName,
          lastName,
          phoneNumber,
          email
        })
      };

      const result = await uploadData({
        file,
        metadata
      });
      const key = result?.path;
      if (!key) return;
      formik.setFieldValue('csvS3Key', result?.path);
    };

    handleColumnsMapping();
  }, [
    formik.errors.csvColumns?.email,
    formik.errors.csvColumns?.firstName,
    formik.errors.csvColumns?.lastName,
    formik.errors.csvColumns?.phoneNumber,

    formik.values.csvColumns.email,
    formik.values.csvColumns.firstName,
    formik.values.csvColumns.lastName,
    formik.values.csvColumns.phoneNumber
  ]);

  const resetFileValues = () => {
    formik.setValues({
      csvColumns: {
        firstName: '',
        lastName: '',
        phoneNumber: '',
        email: ''
      },
      csvS3Key: null
    });
  };

  const onUpload = (file: File) => {
    resetFileValues();
    parseCsv(file);
  };

  const fileName = file?.name || csvImportState?.csvS3Key || null;

  return (
    <Box sx={{ maxWidth: '800px' }}>
      <TextBlock
        variant="subhead"
        title="1. Upload candidate details"
        sx={sectionTitleContainer}
      />
      <Box sx={sectionContainer}>
        <CampaignCreationCSVFileUpload
          fileName={fileName}
          numRows={numRows}
          handleSkip={onCSVImportSkip}
          onUpload={onUpload}
        />
      </Box>
      {!!numRows && (
        <Box mt={3}>
          <TextBlock
            variant="subhead"
            title="2. Locate data"
            sx={sectionTitleContainer}
          />
          <Box sx={sectionContainer}>
            <ColumnsMapper
              columnsConfig={[
                {
                  label: `Phone number ${
                    !isPhoneNumberRequired ? '(optional)' : ''
                  }`,
                  name: 'phoneNumber',
                  previewTitle: 'Preview data for Phone number'
                },
                {
                  label: `Email ${
                    !isEmailRequiredWithFallback ? '(optional)' : ''
                  }`,
                  name: 'email',
                  previewTitle: 'Preview data for Email'
                },
                {
                  label: 'First name',
                  name: 'firstName',
                  previewTitle: 'Preview data for first name'
                },
                {
                  label: 'Last name (optional)',
                  name: 'lastName',
                  previewTitle: 'Preview data for last name'
                }
              ]}
              columns={columns}
              numRows={numRows}
              csvData={csvData}
              successText={`${numRows} recipients found. Choose the columns to be analysed.`}
              // TODO: Fix this typing
              formik={formik as unknown as FormikProps<FormValues>}
            />
          </Box>
        </Box>
      )}
      {fileName && (
        <Box mt={4}>
          <CampaignCreationNextButton
            handleNext={formik.handleSubmit}
            isLoading={!!isUploading}
          />
        </Box>
      )}
    </Box>
  );
};
